"use client";

import type { FormEvent, ReactNode } from "react";
import type { PlaceResult } from "@/lib/api/misc";
import { getKundaliTranslations, type KundaliLang } from "./KundaliFormCard.i18n";
import { KundaliLoadingSteps } from "./KundaliFormCard.loading";
import { FormErrorMessage, KundaliSubmitButton } from "./KundaliFormCard.controls";
import {
  NameField,
  BirthDateField,
  BirthTimeField,
  BirthPlaceField,
} from "./KundaliFormCard.fields";
import { PhoneField, EmailField } from "./KundaliFormCard.contact-fields";
import type { SEOIntentConfig } from "@/lib/seo-intents";
import { reportSummaryByPlanId } from "@/lib/reports-content";
import {
  birthDateInlineError,
  normalizeBirthDateInput,
  type KundaliFieldErrors,
} from "./KundaliFormCard.validation";
import type { FieldFunnel } from "./useFieldFunnel";
import { isValidIndianPhone, isValidOptionalEmail } from "@/lib/phone-india";

// Whether a field's CURRENT value is valid — used to fire field_valid once per
// field (the funnel hook dedupes re-fires). birthDate is valid only when it
// normalizes AND clears the inline bound check; phone/email reuse the canonical
// validators so the funnel and the submit validator agree.
function isFieldValueValid(field: "birthDate" | "phone" | "email", value: string): boolean {
  if (field === "birthDate") {
    return Boolean(normalizeBirthDateInput(value)) && birthDateInlineError(value, "en") === null;
  }
  if (field === "phone") return isValidIndianPhone(value);
  return Boolean(value.trim()) && isValidOptionalEmail(value);
}

export type CapturedSEOEntry = {
  config: SEOIntentConfig;
  sourcePath: string;
  /** The SPECIFIC paid report this visitor arrived to buy (the report landing
   * page's `next_report`), when the entry is a report-buying path. Lets the intent
   * banner restore the paid context that the generic intent config would lose —
   * so a seeker who tapped a ₹199 report CTA doesn't land on a "100% free" screen
   * with no sign of what they came to buy (rules 40/43/46). */
  reportPlanId?: string;
};

export function KundaliInputForm({
  containerClassName,
  onSubmit,
  seoEntry,
  language,
  setLanguage,
  formReady,
  name,
  setName,
  phone,
  setPhone,
  email,
  setEmail,
  requireContact,
  birthDate,
  setBirthDate,
  birthTime,
  setBirthTime,
  requireBirthTime,
  birthPlace,
  setBirthPlace,
  setSelectedPlace,
  selectedPlace,
  onClearPlace,
  ambiguousSuggestion,
  fieldFunnel,
  activeField,
  setActiveField,
  markFormStarted,
  fieldErrors,
  error,
  loading,
  loadingStep,
  reassuranceNote,
  deliveryField,
  submitDisabled,
  retryCard,
  t,
}: {
  containerClassName: string;
  onSubmit: (event: FormEvent) => void;
  seoEntry: CapturedSEOEntry | null;
  language: KundaliLang;
  setLanguage: (language: KundaliLang) => void;
  formReady: boolean;
  name: string;
  setName: (value: string) => void;
  phone: string;
  setPhone: (value: string) => void;
  email: string;
  setEmail: (value: string) => void;
  requireContact: boolean;
  birthDate: string;
  setBirthDate: (value: string) => void;
  birthTime: string;
  setBirthTime: (value: string) => void;
  requireBirthTime: boolean;
  birthPlace: string;
  setBirthPlace: (value: string) => void;
  setSelectedPlace: (place: PlaceResult | null) => void;
  selectedPlace: PlaceResult | null;
  onClearPlace: () => void;
  ambiguousSuggestion: PlaceResult | null;
  fieldFunnel: FieldFunnel;
  activeField: string | null;
  setActiveField: (field: string | null) => void;
  markFormStarted: (field: string) => void;
  fieldErrors: KundaliFieldErrors;
  error: string | null;
  loading: boolean;
  loadingStep: number;
  reassuranceNote?: string;
  deliveryField?: ReactNode;
  submitDisabled?: boolean;
  retryCard?: ReactNode;
  t: ReturnType<typeof getKundaliTranslations>;
}) {
  return (
    <div className={containerClassName}>
      <form onSubmit={onSubmit} noValidate className="card-sacred p-6 sm:p-8 space-y-5">
        <fieldset
          disabled={!formReady}
          aria-busy={!formReady}
          className="m-0 min-w-0 border-0 p-0 space-y-5 disabled:opacity-80"
        >
          <SEOIntentBanner seoEntry={seoEntry} />
          <LanguageToggle language={language} setLanguage={setLanguage} />
          <InputReassurance note={reassuranceNote} />
          <KundaliPersonalFields
            {...{
              name,
              setName,
              phone,
              setPhone,
              email,
              setEmail,
              requireContact,
              birthDate,
              setBirthDate,
              birthTime,
              setBirthTime,
              requireBirthTime,
              activeField,
              setActiveField,
              markFormStarted,
              fieldFunnel,
              language,
              t,
              fieldErrors,
            }}
          />
          <KundaliBirthPlaceField
            {...{
              birthPlace,
              setBirthPlace,
              setSelectedPlace,
              selectedPlace,
              onClearPlace,
              ambiguousSuggestion,
              fieldFunnel,
              activeField,
              setActiveField,
              markFormStarted,
              language,
              t,
              fieldErrors,
            }}
          />
          <FormErrorMessage error={error} />
          {retryCard}
          {deliveryField}
          <KundaliSubmitButton
            loading={loading}
            disabled={submitDisabled}
            label={seoEntry?.config?.ctaLabel || t.submit}
            loadingLabel={t.loading}
          />
          {loading && <KundaliLoadingSteps loadingStep={loadingStep} language={language} />}
          <p className="text-[11px] text-center" style={{ color: "var(--ish-text-faint)" }}>
            {t.savedNote}
          </p>
        </fieldset>
      </form>
    </div>
  );
}

function InputReassurance({ note }: { note?: string }) {
  if (!note) return null;
  return (
    <p className="rounded-md px-3 py-2 text-center text-[12px]" style={reassuranceStyle}>
      {note}
    </p>
  );
}

const reassuranceStyle = {
  background: "var(--ish-bg)",
  border: "1px solid var(--ish-border)",
  color: "var(--ish-text-muted)",
};

function SEOIntentBanner({ seoEntry }: { seoEntry: CapturedSEOEntry | null }) {
  if (!seoEntry) return null;
  const bannerStyle = {
    background: "var(--ish-accent-light)",
    border: "1px solid var(--ish-accent-border)",
    color: "var(--ish-text-muted)",
  } as const;
  const eyebrowStyle = {
    color: "var(--ish-accent)",
    letterSpacing: "0.12em",
  } as const;

  // Paid-report entry: the seeker tapped a specific report CTA (e.g. ₹199 Career
  // Report) and was routed here to build their chart first. Restore the buying
  // context the generic "100% free" hero drops — name the report + its price +
  // make clear checkout opens right here after the chart (rules 40/43/46).
  const report = seoEntry.reportPlanId ? reportSummaryByPlanId(seoEntry.reportPlanId) : null;
  if (report) {
    return (
      <div className="rounded-lg p-3.5 text-[13px]" style={bannerStyle}>
        <p
          className="mb-1 flex items-center gap-1.5 text-[11px] font-semibold uppercase"
          style={eyebrowStyle}
        >
          <span aria-hidden>ॐ</span> Unlocking · {report.name} · ₹{report.priceInr}
        </p>
        <p className="font-semibold" style={{ color: "var(--ish-dark)" }}>
          {seoEntry.config.prompt}
        </p>
        <p className="mt-1 text-[12px]">
          Enter your birth details below — I build your chart first, then your {report.name} (₹
          {report.priceInr}) opens right here. No separate page, no waiting.
        </p>
      </div>
    );
  }

  return (
    <div className="rounded-lg p-3.5 text-[13px]" style={bannerStyle}>
      <p
        className="mb-1 flex items-center gap-1.5 text-[11px] font-semibold uppercase"
        style={eyebrowStyle}
      >
        <span aria-hidden>ॐ</span> Pandit Aaditya · Your question
      </p>
      <p className="font-semibold" style={{ color: "var(--ish-dark)" }}>
        {seoEntry.config.prompt}
      </p>
      <p className="mt-1 text-[12px]">
        I&apos;ll read your chart to answer this. Share your birth details below — your question
        stays open and I&apos;ll continue it the moment your kundali is ready.
      </p>
    </div>
  );
}

function LanguageToggle({
  language,
  setLanguage,
}: {
  language: KundaliLang;
  setLanguage: (language: KundaliLang) => void;
}) {
  return (
    <div className="flex justify-center">
      <div
        role="tablist"
        aria-label="Language"
        className="flex p-1 rounded-full"
        style={{ background: "var(--ish-bg)", border: "1px solid var(--ish-border)" }}
      >
        {(["en", "hi"] as KundaliLang[]).map((lang) => (
          <button
            key={lang}
            type="button"
            role="tab"
            aria-selected={language === lang}
            onClick={() => setLanguage(lang)}
            className="px-4 py-1.5 rounded-full text-[12px] font-semibold transition-all duration-200"
            style={{
              color: language === lang ? "#fff" : "var(--ish-text-muted)",
              backgroundColor: language === lang ? "var(--ish-accent)" : "transparent",
            }}
          >
            {lang === "en" ? "English" : "हिंदी"}
          </button>
        ))}
      </div>
    </div>
  );
}

function KundaliPersonalFields({
  name,
  setName,
  phone,
  setPhone,
  email,
  setEmail,
  requireContact,
  birthDate,
  setBirthDate,
  birthTime,
  setBirthTime,
  requireBirthTime,
  activeField,
  setActiveField,
  markFormStarted,
  fieldFunnel,
  language,
  t,
  fieldErrors,
}: {
  name: string;
  setName: (value: string) => void;
  phone: string;
  setPhone: (value: string) => void;
  email: string;
  setEmail: (value: string) => void;
  requireContact: boolean;
  birthDate: string;
  setBirthDate: (value: string) => void;
  birthTime: string;
  setBirthTime: (value: string) => void;
  requireBirthTime: boolean;
  activeField: string | null;
  setActiveField: (field: string | null) => void;
  markFormStarted: (field: string) => void;
  fieldFunnel: FieldFunnel;
  language: KundaliLang;
  t: ReturnType<typeof getKundaliTranslations>;
  fieldErrors: KundaliFieldErrors;
}) {
  // Engaging a field fires BOTH the legacy global form-started AND the per-field
  // funnel `start` (deduped in the hook) so the field funnel can measure
  // start → valid → submit → outcome per field.
  const onFieldFocus = (field: Parameters<FieldFunnel["start"]>[0]) => {
    markFormStarted(field);
    fieldFunnel.start(field);
  };
  return (
    <>
      <NameField
        value={name}
        onChange={(v) => {
          setName(v);
          if (v.trim()) fieldFunnel.valid("name");
        }}
        onFocus={() => onFieldFocus("name")}
        onBlur={() => setActiveField(null)}
        activeField={activeField}
        language={language}
        t={t}
        error={fieldErrors.name}
      />
      <PhoneField
        value={phone}
        onChange={(v) => {
          setPhone(v);
          if (isFieldValueValid("phone", v)) fieldFunnel.valid("phone");
        }}
        onFocus={() => onFieldFocus("phone")}
        onBlur={() => setActiveField(null)}
        activeField={activeField}
        t={t}
        required={requireContact}
        autoFocus={requireContact}
        error={fieldErrors.phone}
      />
      <EmailField
        value={email}
        onChange={(v) => {
          setEmail(v);
          if (isFieldValueValid("email", v)) fieldFunnel.valid("email");
        }}
        onFocus={() => onFieldFocus("email")}
        onBlur={() => setActiveField(null)}
        activeField={activeField}
        t={t}
        error={fieldErrors.email}
      />
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <BirthDateField
          value={birthDate}
          onChange={(v) => {
            setBirthDate(v);
            if (isFieldValueValid("birthDate", v)) fieldFunnel.valid("birthDate");
          }}
          onFocus={() => onFieldFocus("birthDate")}
          onBlur={() => setActiveField(null)}
          activeField={activeField}
          language={language}
          t={t}
          error={fieldErrors.birthDate}
        />
        <BirthTimeField
          value={birthTime}
          onChange={(v) => {
            setBirthTime(v);
            if (v.trim()) fieldFunnel.valid("birthTime");
          }}
          onFocus={() => onFieldFocus("birthTime")}
          onBlur={() => setActiveField(null)}
          activeField={activeField}
          language={language}
          t={t}
          required={requireBirthTime}
          error={fieldErrors.birthTime}
        />
      </div>
    </>
  );
}

function KundaliBirthPlaceField({
  birthPlace,
  setBirthPlace,
  setSelectedPlace,
  selectedPlace,
  onClearPlace,
  ambiguousSuggestion,
  fieldFunnel,
  activeField,
  setActiveField,
  markFormStarted,
  language,
  t,
  fieldErrors,
}: {
  birthPlace: string;
  setBirthPlace: (value: string) => void;
  setSelectedPlace: (place: PlaceResult | null) => void;
  selectedPlace: PlaceResult | null;
  onClearPlace: () => void;
  ambiguousSuggestion: PlaceResult | null;
  fieldFunnel: FieldFunnel;
  activeField: string | null;
  setActiveField: (field: string | null) => void;
  markFormStarted: (field: string) => void;
  language: KundaliLang;
  t: ReturnType<typeof getKundaliTranslations>;
  fieldErrors: KundaliFieldErrors;
}) {
  return (
    <BirthPlaceField
      value={birthPlace}
      onChange={(val) => {
        setBirthPlace(val);
        setSelectedPlace(null);
      }}
      onSelect={(place) => {
        // A tapped suggestion (dropdown OR "did you mean") commits the chip —
        // this IS the placeSelected=true confirmation, so field_valid fires.
        setBirthPlace(place.display);
        setSelectedPlace(place);
        fieldFunnel.valid("birthPlace");
      }}
      onFocus={() => markFormStarted("birthPlace")}
      onBlur={() => setActiveField(null)}
      activeField={activeField}
      language={language}
      t={t}
      error={fieldErrors.birthPlace}
      selectedPlace={selectedPlace}
      onClearPlace={onClearPlace}
      ambiguousSuggestion={ambiguousSuggestion}
    />
  );
}
